/**
 * The New Loan form.
 *
 * The schedule preview is computed by the SAME shared function the API saves
 * with (`buildLoanSchedule`), so the totals shown here are the totals the loan
 * will have. Nothing is saved until the person chooses Save draft or Issue.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUp, Search, X } from 'lucide-react';
import {
  LOAN_CATEGORY_LABELS,
  LOAN_COUNTERPARTY_LABELS,
  LOAN_DIRECTION_LABELS,
  LOAN_FREQUENCIES,
  LOAN_FREQUENCY_LABELS,
  LOAN_EARLY_REPAYMENT_LABELS,
  LOAN_EARLY_REPAYMENT_POLICIES,
  LOAN_FEE_COLLECTIONS,
  LOAN_FEE_COLLECTION_LABELS,
  LOAN_INTEREST_TYPES,
  LOAN_INTEREST_TYPE_LABELS,
  LOAN_LATE_FEE_LABELS,
  LOAN_LATE_FEE_TYPES,
  lendingRefusal,
  type LoanEarlyRepaymentPolicy,
  type LoanFeeCollection,
  type LoanLateFeeType,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  buildLoanSchedule,
  loanTermsSchema,
  loanWording,
  type LoanBorrowerMatch,
  type LoanCategory,
  type LoanCounterpartyKind,
  type LoanDirection,
  type LoanFrequency,
  type LoanInput,
  type LoanInterestType,
  type LoanSchedule,
  type PaymentMethod,
} from '@cenox/shared';
import { AccountSelect } from '../money/AccountSelect';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRecordLocation } from '../../lib/locationContext';
import { money, todayIn } from '../../lib/format';
import { LocationField } from '../../components/LocationField';
import { Button, Input, Modal, Select, Textarea, cn } from '../../components/ui';
import {
  newSubmissionKey,
  useBorrowerSearch,
  useCreateLoan,
  useLendingPolicies,
  useLoanPurposes,
  useUploadLoanDocument,
} from './data';
import { day, useHasRiders, useLoanCategories } from './parts';

function plusDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d + days));
  return at.toISOString().slice(0, 10);
}

const KIND_FOR_CATEGORY: Partial<Record<LoanCategory, LoanCounterpartyKind>> = {
  rider: 'rider',
  vehicle: 'rider',
  staff: 'staff',
};

const EARLY_SETTLEMENT_HINTS: Record<LoanEarlyRepaymentPolicy, string> = {
  no_rebate: 'Settling early pays all the interest scheduled.',
  rebate_future_interest: 'Unpaid interest on every instalment not yet due is waived.',
  rebate_prorated_daily: 'Interest is charged for the days passed in the current instalment, and none after.',
};

export function LoanFormModal({
  open,
  onClose,
  defaultDirection = 'receivable',
  defaultCategory,
}: {
  open: boolean;
  onClose: () => void;
  defaultDirection?: LoanDirection;
  defaultCategory?: LoanCategory | undefined;
}): ReactNode {
  const { business } = useAuth();
  const navigate = useNavigate();
  const timezone = business?.timezone ?? 'UTC';
  const today = todayIn(timezone);
  const location = useRecordLocation();
  const purposes = useLoanPurposes();
  const create = useCreateLoan();
  const upload = useUploadLoanDocument();

  const riders = useHasRiders();
  const categories = useLoanCategories();
  const initialCategory: LoanCategory =
    defaultDirection === 'payable'
      ? 'operational'
      : defaultCategory && categories.includes(defaultCategory)
        ? defaultCategory
        : riders
          ? 'rider'
          : 'staff';
  const [direction, setDirection] = useState<LoanDirection>(defaultDirection);
  const [category, setCategory] = useState<LoanCategory>(initialCategory);
  const [kind, setKind] = useState<LoanCounterpartyKind>(
    defaultDirection === 'payable' ? 'organisation' : KIND_FOR_CATEGORY[initialCategory] ?? 'individual',
  );
  const [linked, setLinked] = useState<LoanBorrowerMatch | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [purpose, setPurpose] = useState('');
  const [otherPurpose, setOtherPurpose] = useState('');
  const [principal, setPrincipal] = useState('');
  const [interestType, setInterestType] = useState<LoanInterestType>('none');
  const [rate, setRate] = useState('');
  const [issuedOn, setIssuedOn] = useState(today);
  const [firstDueOn, setFirstDueOn] = useState(plusDays(today, 7));
  const [frequency, setFrequency] = useState<LoanFrequency>('weekly');
  const [intervalDays, setIntervalDays] = useState('10');
  const [count, setCount] = useState('10');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [processingFee, setProcessingFee] = useState('');
  const [feeCollection, setFeeCollection] = useState<LoanFeeCollection>('upfront');
  const [earlyChoice, setEarlyPolicy] = useState<LoanEarlyRepaymentPolicy | null>(null);
  const [lateFeeType, setLateFeeType] = useState<LoanLateFeeType>('none');
  const [lateFeeValue, setLateFeeValue] = useState('');
  const [graceDays, setGraceDays] = useState('3');
  // One key for this form: a double-clicked Issue makes one loan.
  const [submissionKey] = useState(newSubmissionKey);
  const policies = useLendingPolicies();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  /*
    Opened before the modules were known, the form may have started on a rider
    loan; once it is clear this business has no riders, move to one it can make.
  */
  useEffect(() => {
    if (direction !== 'receivable' || categories.includes(category)) return;
    setCategory('staff');
    setKind('staff');
    setLinked(null);
  }, [categories, category, direction]);

  const words = loanWording(direction);
  const currency = business?.currencyCode ?? 'NGN';
  const kinds: LoanCounterpartyKind[] =
    direction === 'payable'
      ? ['individual', 'organisation']
      : riders
        ? ['rider', 'staff', 'individual', 'organisation']
        : ['staff', 'individual', 'organisation'];

  const terms = {
    principal,
    interestType,
    interestRate: interestType === 'none' ? '0' : rate,
    frequency,
    intervalDays: frequency === 'custom' ? Number(intervalDays) : null,
    instalmentCount: Number(count),
    issuedOn,
    firstDueOn,
    processingFee: processingFee.trim() || '0',
    feeCollection,
  };
  const parsed = loanTermsSchema.safeParse(terms);
  const schedule: LoanSchedule | null = useMemo(() => {
    if (!parsed.success) return null;
    try {
      return buildLoanSchedule(parsed.data);
    } catch {
      return null;
    }
  }, [parsed.success, parsed.success ? JSON.stringify(parsed.data) : '']); // eslint-disable-line react-hooks/exhaustive-deps
  const termIssue = parsed.success ? null : parsed.error.issues[0];

  const fieldError = (field: string) => (error instanceof ApiError ? error.fieldError(field) : undefined);

  function chooseDirection(next: LoanDirection) {
    setDirection(next);
    setLinked(null);
    if (next === 'payable') {
      setKind('organisation');
      setCategory('operational');
    } else {
      setKind(KIND_FOR_CATEGORY[category] ?? 'individual');
    }
  }

  function chooseCategory(next: LoanCategory) {
    setCategory(next);
    if (direction === 'receivable' && !linked) setKind(KIND_FOR_CATEGORY[next] ?? kind);
  }

  function chooseBorrower(match: LoanBorrowerMatch) {
    setLinked(match);
    setName(match.name);
    setPhone(match.phone ?? '');
    if (match.branchId && location.locations.some((l) => l.id === match.branchId)) {
      location.setBranchId(match.branchId);
    }
  }

  const needsLink = kind === 'rider' || kind === 'staff';

  // The same rule the API applies, so the form says why before it is sent.
  const countryCode =
    location.locations.find((l) => l.id === location.branchId)?.countryCode ?? business?.countryCode ?? 'NG';
  const policy = policies.data?.policies.find((p) => p.countryCode === countryCode) ?? null;
  /*
    Lending with interest offers only the early-settlement terms the owner
    declared for the country, starting on their default. Without interest
    there is nothing to rebate, and borrowing follows the lender's terms.
  */
  const offered: readonly LoanEarlyRepaymentPolicy[] =
    direction === 'receivable' && interestType !== 'none' && policy?.lendingEnabled
      ? policy.earlySettlementOptions
      : LOAN_EARLY_REPAYMENT_POLICIES;
  const earlyPolicy: LoanEarlyRepaymentPolicy =
    earlyChoice && offered.includes(earlyChoice)
      ? earlyChoice
      : policy && offered.includes(policy.defaultEarlySettlement)
        ? policy.defaultEarlySettlement
        : offered[0]!;
  const refusal = policies.data
    ? lendingRefusal(
        {
          direction,
          counterpartyKind: kind,
          interestType,
          interestRate: interestType === 'none' ? '0' : rate || '0',
          processingFee: /^\d+(\.\d{1,2})?$/.test(processingFee.trim()) ? processingFee.trim() : '0',
          lateFeeType,
          earlyRepaymentPolicy: earlyPolicy,
        },
        policy,
        countryCode,
      )
    : null;
  const purposeValue = purpose === '__other' ? otherPurpose.trim() : purpose;

  async function submit(status: 'draft' | 'active', event?: FormEvent) {
    event?.preventDefault();
    setError(null);
    try {
      let attachmentId: string | null = null;
      if (file) attachmentId = (await upload.mutateAsync(file)).id;
      const body: LoanInput = {
        direction,
        category,
        counterpartyKind: kind,
        counterpartyName: name.trim(),
        counterpartyPhone: phone.trim() || null,
        riderId: kind === 'rider' ? linked?.id ?? null : null,
        staffId: kind === 'staff' ? linked?.id ?? null : null,
        branchId: location.branchId || null,
        purpose: purposeValue || null,
        paymentMethod: method,
        ...(accountId ? { accountId } : {}),
        notes: notes.trim() || null,
        attachmentId,
        principal,
        interestType,
        interestRate: interestType === 'none' ? '0' : rate,
        frequency,
        intervalDays: frequency === 'custom' ? Number(intervalDays) : null,
        instalmentCount: Number(count),
        issuedOn,
        firstDueOn,
        processingFee: processingFee.trim() || '0',
        feeCollection,
        earlyRepaymentPolicy: earlyPolicy,
        lateFeeType,
        lateFeeValue: lateFeeType === 'none' ? '0' : lateFeeValue.trim(),
        lateFeeGraceDays: Number(graceDays) || 0,
        status,
        idempotencyKey: submissionKey,
      };
      const loan = await create.mutateAsync(body);
      onClose();
      navigate(`/loans/${loan.id}`);
    } catch (e) {
      setError(e as Error);
    }
  }

  const busy = create.isPending || upload.isPending;
  const canSubmit =
    Boolean(schedule) && name.trim().length > 0 && (!needsLink || linked) && !location.unresolved && !refusal;

  return (
    <Modal open={open} onClose={onClose} title={direction === 'receivable' ? 'New loan' : 'New borrowing'} size="lg">
      <form onSubmit={(e) => void submit('active', e)} className="space-y-5" noValidate>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="radiogroup" aria-label="Lent or borrowed" className="inline-flex rounded-lg border border-slate-200 p-1">
            {(['receivable', 'payable'] as const).map((d) => (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={direction === d}
                onClick={() => chooseDirection(d)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  direction === d ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50',
                )}
              >
                {LOAN_DIRECTION_LABELS[d]}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Reference: <span className="font-medium text-slate-700">assigned on save (LOA-…)</span>
          </p>
        </div>
        <p className="-mt-2 text-xs text-slate-500">
          {direction === 'receivable'
            ? 'Money the business lends. It becomes a receivable, never an expense.'
            : 'Money the business borrows. It becomes a liability, never revenue.'}
        </p>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-slate-900">{words.counterparty}</legend>
          <Select
            label="Loan category"
            value={category}
            onChange={(e) => chooseCategory(e.target.value as LoanCategory)}
            options={categories.map((c) => ({ value: c, label: LOAN_CATEGORY_LABELS[c].replace(/ loans$/, '') }))}
            error={fieldError('category')}
          />
          <Select
            label={`${words.counterparty} type`}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as LoanCounterpartyKind);
              setLinked(null);
            }}
            options={kinds.map((k) => ({ value: k, label: LOAN_COUNTERPARTY_LABELS[k] }))}
            error={fieldError('counterpartyKind')}
          />

          {needsLink && (
            <div className="sm:col-span-2">
              <BorrowerPicker kind={kind as 'rider' | 'staff'} linked={linked} onPick={chooseBorrower} onClear={() => setLinked(null)} />
              {(fieldError('riderId') || fieldError('staffId')) && (
                <p className="mt-1 text-xs font-medium text-red-600" role="alert">{fieldError('riderId') ?? fieldError('staffId')}</p>
              )}
            </div>
          )}

          <Input
            label={`${words.counterparty} name`}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldError('counterpartyName')}
            autoComplete="off"
          />
          <Input
            label="Phone number"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={fieldError('counterpartyPhone')}
          />
          <div className="sm:col-span-2">
            <LocationField
              value={location.branchId}
              onChange={location.setBranchId}
              locations={location.locations}
              error={fieldError('branchId')}
              label="Branch / location"
            />
          </div>
          <Select
            label="Purpose"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Choose a purpose"
            options={[
              ...(purposes.data ?? []).map((p) => ({ value: p.name, label: p.name })),
              { value: '__other', label: 'Other…' },
            ]}
          />
          {purpose === '__other' && (
            <Input label="Other purpose" value={otherPurpose} onChange={(e) => setOtherPurpose(e.target.value)} />
          )}
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-slate-900">Terms</legend>
          <Input
            label={`Principal (${currency})`}
            required
            inputMode="decimal"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            error={fieldError('principal')}
            placeholder="100000.00"
          />
          <Select
            label="Interest"
            value={interestType}
            onChange={(e) => setInterestType(e.target.value as LoanInterestType)}
            options={LOAN_INTEREST_TYPES.map((t) => ({ value: t, label: LOAN_INTEREST_TYPE_LABELS[t] }))}
          />
          {interestType !== 'none' && (
            <Input
              label="Interest rate (% a year)"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              error={fieldError('interestRate')}
              hint={interestType === 'flat' ? 'Charged on the full principal for the whole term' : 'Charged on the balance still owed'}
            />
          )}
          <Select
            label="Payment method"
            value={method}
            onChange={(e) => { setMethod(e.target.value as PaymentMethod); setAccountId(''); }}
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
          />
          <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fieldError('accountId')} />
          <Input label="Loan issue date" type="date" required value={issuedOn} max={today} onChange={(e) => setIssuedOn(e.target.value)} error={fieldError('issuedOn')} />
          <Input
            label="First repayment date"
            type="date"
            required
            value={firstDueOn}
            min={issuedOn}
            onChange={(e) => setFirstDueOn(e.target.value)}
            error={fieldError('firstDueOn')}
          />
          <Select
            label="Repayment frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as LoanFrequency)}
            options={LOAN_FREQUENCIES.map((f) => ({ value: f, label: LOAN_FREQUENCY_LABELS[f] }))}
          />
          {frequency === 'custom' && (
            <Input label="Days between instalments" inputMode="numeric" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} error={fieldError('intervalDays')} />
          )}
          <Input
            label="Number of instalments"
            inputMode="numeric"
            required
            value={count}
            onChange={(e) => setCount(e.target.value)}
            error={fieldError('instalmentCount')}
          />
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-slate-900">Fees and early repayment</legend>
          <Input
            label={`Processing fee (${currency})`}
            inputMode="decimal"
            value={processingFee}
            onChange={(e) => setProcessingFee(e.target.value)}
            placeholder="0.00"
            error={fieldError('processingFee')}
            hint="Optional. Finance income, never revenue."
          />
          {processingFee.trim() && Number(processingFee) > 0 && (
            <Select
              label="Fee is"
              value={feeCollection}
              onChange={(e) => setFeeCollection(e.target.value as LoanFeeCollection)}
              options={LOAN_FEE_COLLECTIONS.map((f) => ({ value: f, label: LOAN_FEE_COLLECTION_LABELS[f] }))}
            />
          )}
          <Select
            label="If settled early"
            value={earlyPolicy}
            onChange={(e) => setEarlyPolicy(e.target.value as LoanEarlyRepaymentPolicy)}
            options={offered.map((p) => ({ value: p, label: LOAN_EARLY_REPAYMENT_LABELS[p] }))}
            hint={EARLY_SETTLEMENT_HINTS[earlyPolicy]}
          />
          <Select
            label="Late payment"
            value={lateFeeType}
            onChange={(e) => setLateFeeType(e.target.value as LoanLateFeeType)}
            options={LOAN_LATE_FEE_TYPES.map((t) => ({ value: t, label: LOAN_LATE_FEE_LABELS[t] }))}
            hint="Applied by a person to a late instalment, never automatically."
          />
          {lateFeeType !== 'none' && (
            <>
              <Input
                label={lateFeeType === 'fixed' ? `Late fee (${currency})` : 'Late fee (% of the instalment)'}
                inputMode="decimal"
                value={lateFeeValue}
                onChange={(e) => setLateFeeValue(e.target.value)}
                error={fieldError('lateFeeValue')}
              />
              <Input
                label="Grace days"
                inputMode="numeric"
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                error={fieldError('lateFeeGraceDays')}
              />
            </>
          )}
        </fieldset>

        {refusal && (
          <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {refusal}
          </p>
        )}

        <section aria-label="Repayment schedule preview" className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Repayment schedule</h3>
          {schedule ? (
            <>
              <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <PreviewFigure label="Instalment" value={money(schedule.instalmentAmount, currency)} />
                <PreviewFigure label="Total interest" value={money(schedule.totalInterest, currency)} />
                {Number(schedule.totalFees) > 0 && <PreviewFigure label="Fees" value={money(schedule.totalFees, currency)} />}
                <PreviewFigure label="Total payable" value={money(schedule.totalPayable, currency)} strong />
                <PreviewFigure label="Final payment" value={day(schedule.finalDueOn)} />
              </dl>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[420px] text-xs">
                  <thead className="text-left text-slate-500">
                    <tr>
                      <th className="py-1 pr-2 font-medium">#</th>
                      <th className="py-1 pr-2 font-medium">Due</th>
                      <th className="py-1 pr-2 text-right font-medium">Principal</th>
                      <th className="py-1 pr-2 text-right font-medium">Interest</th>
                      {Number(schedule.totalFees) > 0 && <th className="py-1 pr-2 text-right font-medium">Fee</th>}
                      <th className="py-1 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums text-slate-700">
                    {(showAll ? schedule.instalments : schedule.instalments.slice(0, 4)).map((i) => (
                      <tr key={i.sequence}>
                        <td className="py-0.5 pr-2">{i.sequence}</td>
                        <td className="py-0.5 pr-2">{day(i.dueOn)}</td>
                        <td className="py-0.5 pr-2 text-right">{money(i.principal, currency)}</td>
                        <td className="py-0.5 pr-2 text-right">{money(i.interest, currency)}</td>
                        {Number(schedule.totalFees) > 0 && <td className="py-0.5 pr-2 text-right">{money(i.fee, currency)}</td>}
                        <td className="py-0.5 text-right">{money(i.total, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {schedule.instalments.length > 4 && (
                  <button type="button" className="mt-1 text-xs font-medium text-brand-700 hover:underline" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? 'Show fewer' : `Show all ${schedule.instalments.length} instalments`}
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className="mt-2 text-sm text-slate-500">
              {principal ? termIssue?.message ?? 'Complete the terms to see the schedule.' : 'Enter the principal and terms to see the schedule.'}
            </p>
          )}
        </section>

        <div className="grid grid-cols-1 gap-3">
          <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div>
            <span className="label-base">Supporting document (optional)</span>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
              <FileUp className="size-4" aria-hidden />
              <span className="truncate">{file ? file.name : 'Attach a signed agreement, PDF or photo'}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                className="sr-only"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="button" variant="secondary" disabled={!canSubmit || busy} onClick={() => void submit('draft')}>
            Save draft
          </Button>
          <Button type="submit" loading={busy} disabled={!canSubmit}>
            {direction === 'receivable' ? 'Issue loan' : 'Record borrowing'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PreviewFigure({ label, value, strong = false }: { label: string; value: string; strong?: boolean }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={cn('tabular-nums', strong ? 'font-semibold text-slate-900' : 'text-slate-800')}>{value}</dd>
    </div>
  );
}

/** Find a rider or staff member by name, phone number or ID. */
function BorrowerPicker({
  kind,
  linked,
  onPick,
  onClear,
}: {
  kind: 'rider' | 'staff';
  linked: LoanBorrowerMatch | null;
  onPick: (m: LoanBorrowerMatch) => void;
  onClear: () => void;
}): ReactNode {
  const [q, setQ] = useState('');
  const results = useBorrowerSearch(linked ? null : kind, q);
  const noun = kind === 'rider' ? 'rider' : 'staff member';

  if (linked) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900">{linked.name}</p>
          <p className="truncate text-xs text-slate-600">
            {[linked.detail, linked.phone].filter(Boolean).join(' · ') || `Linked ${noun}`}
          </p>
        </div>
        <button type="button" onClick={onClear} aria-label={`Choose a different ${noun}`} className="rounded p-1 text-slate-500 hover:bg-white">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className="label-base" htmlFor={`find-${kind}`}>
        Find the {noun} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          id={`find-${kind}`}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={kind === 'rider' ? 'Name, phone or Rider ID' : 'Name, phone or staff ID'}
          className="input-base pl-9"
          autoComplete="off"
        />
      </div>
      <ul role="listbox" aria-label={`Matching ${noun}s`} className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-white">
        {results.isPending ? (
          <li className="px-3 py-2 text-sm text-slate-500">Searching…</li>
        ) : (results.data ?? []).length === 0 ? (
          <li className="px-3 py-2 text-sm text-slate-500">No {noun} matches.</li>
        ) : (
          results.data!.map((m) => (
            <li key={m.id} role="option" aria-selected={false}>
              <button type="button" onClick={() => onPick(m)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50">
                <span className="truncate text-sm text-slate-900">{m.name}</span>
                <span className="shrink-0 text-xs text-slate-500">{[m.detail, m.phone].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
